/**
 * Continue Practice Widget
 * "Today's Pick" or "Free Practice" quick-launch card.
 * Moved from StudentDashboardBeta (Phase 3.2 extraction).
 */

import React from 'react'
import { useNavigate } from 'react-router-dom'
import { Play } from 'lucide-react'
import { useTranslation } from '../../i18n/TranslationProvider'

export interface ContinuePracticeWidgetProps {
  todayPick?: {
    title: string
    description: string
  } | null
  className?: string
}

const ProgressBar: React.FC<{ pct: number; color?: string }> = ({
  pct,
  color = 'linear-gradient(90deg, var(--color-cyan), var(--color-neon))',
}) => (
  <div className="h-[5px] bg-[var(--color-g200)] rounded-[5px] overflow-hidden mt-[10px]">
    <div
      className="h-full rounded-[5px] transition-[width] duration-600"
      style={{ width: `${Math.min(100, pct)}%`, background: color }}
    />
  </div>
)

const ContinuePracticeWidget: React.FC<ContinuePracticeWidgetProps> = ({
  todayPick,
  className = '',
}) => {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const hasPick = todayPick && todayPick.title

  return (
    <div
      onClick={() => navigate('/dashboard/practice')}
      className={`bg-white border border-[var(--color-border-light)] rounded-[var(--radius-r)]
                  p-[18px] shadow-[var(--shadow-sh0)]
                  transition-all duration-[.2s]
                  cursor-pointer hover:shadow-[var(--shadow-sh2)] hover:-translate-y-[2px]
                  active:translate-y-0 active:scale-[.99]
                  flex flex-col ${className}`}
    >
      <div className="flex items-center gap-[7px] text-[11px] font-semibold text-[var(--color-text-tertiary)]
                      uppercase tracking-[.5px] mb-[12px]">
        <Play size={13} />
        {hasPick ? t('student.continue', "Today's Pick") : t('student.continue', 'Practice')}
      </div>
      {hasPick ? (
        <>
          <div className="text-[14px] font-bold text-[var(--color-text-primary)]">
            {todayPick.title}
          </div>
          <div className="text-[11px] text-[var(--color-text-tertiary)] mt-[1px]">
            {todayPick.description}
          </div>
          <ProgressBar
            pct={68}
            color="linear-gradient(90deg, var(--color-amber-accent), var(--color-gold))"
          />
        </>
      ) : (
        <>
          <div className="text-[14px] font-bold text-[var(--color-text-primary)]">
            Free Practice
          </div>
          <div className="text-[11px] text-[var(--color-text-tertiary)] mt-[1px]">
            Choose a preset to get started
          </div>
          <ProgressBar pct={0} />
        </>
      )}
    </div>
  )
}

export default ContinuePracticeWidget
